import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch } from '../../../redux/store';
import { ChangePasswordRequestModel } from '../../../redux/auth/_authTypes';
import { changePassword } from '../../../redux/auth/passwordSlice';
import { handleError } from '../../../utils/handleError';
import { useNavigate } from 'react-router';
import { logoutSession } from '../../../redux/auth/authSlice';
import { KTIcon } from '../../../../_metronic/helpers';

const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+=\[{\]};:<>|./?,-]).{12,}$/;

const ChangePassword = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [hasErrors, setHasErrors] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const passwordFormValidationSchema = useMemo(
        () =>
            Yup.object().shape({
                currentPassword: Yup.string()
                    .required(intl.formatMessage({ id: 'AUTH.PASSWORD.CURRENT_PASSWORD_REQUIRED' })),
                newPassword: Yup.string()
                    .matches(passwordRegex, intl.formatMessage({ id: 'AUTH.PASSWORD.PATTERN' }))
                    .notOneOf([Yup.ref('currentPassword')], intl.formatMessage({ id: 'AUTH.PASSWORD.CANNOT_BE_SAME_AS_CURRENT' }))
                    .required(intl.formatMessage({ id: 'AUTH.PASSWORD.NEW_PASSWORD_REQUIRED' })),
                passwordConfirmation: Yup.string()
                    .oneOf([Yup.ref('newPassword')], intl.formatMessage({ id: 'AUTH.PASSWORD.PASSWORDS_MUST_MATCH' }))
                    .required(intl.formatMessage({ id: 'AUTH.PASSWORD.CONFIRMATION_PASSWORD_REQUIRED' })),
            }),
        [intl]
    );

    const formik = useFormik<ChangePasswordRequestModel>({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            passwordConfirmation: '',
        },
        validationSchema: passwordFormValidationSchema,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            try {
                setLoading(true);
                const response = await dispatch(changePassword(values)).unwrap();
                if (response) {
                    setStatus(intl.formatMessage({ id: 'AUTH.PASSWORD.CHANGE_SUCCESS' }));
                    setHasErrors(false);

                    await dispatch(logoutSession());
                    navigate('/auth/login', { replace: true });
                }
            } catch (error) {
                const errorMessage = handleError(error);
                setHasErrors(true);
                setStatus(errorMessage);
            }
            finally {
                setSubmitting(false);
                setLoading(false);
            }
        },
    });

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const renderPasswordInput = (field: keyof ChangePasswordRequestModel, labelId: string) => (
        <div className=''>
            <div className='fv-row mb-0'>
                <label className='form-label fs-6 fw-bolder mb-3'>
                    <FormattedMessage id={labelId} />
                </label>
                <div className="input-group">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete='off'
                        className={clsx(
                            'form-control form-control-lg form-control-solid',
                            {
                                'is-invalid': formik.touched[field] && formik.errors[field],
                            },
                            {
                                'is-valid': formik.touched[field] && !formik.errors[field],
                            }
                        )}
                        {...formik.getFieldProps(field)}
                    />
                    <button
                        type="button"
                        className="btn btn-light"
                        onClick={togglePasswordVisibility}
                    >
                        <KTIcon iconName={showPassword ? 'eye-slash' : 'eye'} className='fs-3' />
                    </button>
                </div>
                {formik.touched[field] && formik.errors[field] && (
                    <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors[field]}</div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className='d-flex flex-wrap align-items-center mb-1'>

            <div className='flex-row-fluid '>

                <div className='text-center mb-15'>
                    <h1 className='text-gray-900 fw-bolder mb-3'>
                        <FormattedMessage id="AUTH.PASSWORD.CHANGE_PASSWORD" />
                    </h1>
                    <div className='text-gray-500 fw-semibold fs-6'>
                        <FormattedMessage id="AUTH.PASSWORD.CHANGE_PASSWORD_SUBTITLE" />
                    </div>
                </div>

                {formik.status && (
                    <div className={`mb-lg-10 alert ${hasErrors === false ? 'alert-info' : 'alert-danger'}`}>
                        <div className='alert-text font-weight-bold'>{formik.status}</div>
                    </div>
                )}

                <form
                    onSubmit={formik.handleSubmit}
                    className='form'
                    noValidate
                >
                    <div className='fv-row mb-5 '>
                        {renderPasswordInput('currentPassword', 'AUTH.PASSWORD.CURRENT_PASSWORD')}
                    </div>
                    <div className='fv-row mb-5'>
                        {renderPasswordInput('newPassword', 'AUTH.PASSWORD.NEW_PASSWORD')}
                    </div>
                    <div className='fv-row mb-5'>
                        {renderPasswordInput('passwordConfirmation', 'AUTH.PASSWORD.CONFIRM_NEW_PASSWORD')}
                    </div>

                    <div className='d-flex justify-content-end mt-10'>
                        <button
                            type='submit'
                            className='btn btn-primary me-2 px-6'
                            disabled={loading}
                        >
                            {!loading && <FormattedMessage id='AUTH.PASSWORD.UPDATE_PASSWORD' />}
                            {loading && (
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    <FormattedMessage id='GENERAL.PLEASE_WAIT' />{' '}
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </form>
            </div>

        </div>
    );
};


export { ChangePassword };
